/**
 * ReportTypeSelectStep — Step 1 of NewReportWizard.
 *
 * Type cards share the same layout as the Reports-tab empty state
 * (`ReportTypeLaunchCard`). The wizard adds an optional version chip
 * when the site already has a report of that type.
 */
import React from 'react';
import { StatusPill } from '../ui/status-pill';
import {
  DECISION_STAGE_LABEL,
  getAllReportTypes,
  isLiveOnlyReportType,
  type DecisionStage,
  type ReportTypeId,
} from '../../config/reportTypes';
import type { ProjectIntent, SiteRagReport } from '../../types/siteRag';
import type { ReportPlanItem } from '../../lib/pathToGo/reportPlan';
import { analysisLane, laterLabel } from '../../lib/pathToGo/reportPlanLanes';
import { ReportTypeLaunchCard } from './ReportTypeLaunchCard';
import { useIsDatacrateAdmin } from '../../lib/isDatacrateAdmin';

/** Header for report types outside both stages — the Analyses tab's section name. */
const ADDITIONAL_ANALYSES_LABEL = 'Additional analyses';

interface Props {
  /** Kept for backwards-compat; the wizard now controls visibility via
   *  WizardFrame's `open` prop. The step always renders its body when
   *  this is undefined or true. */
  open?: boolean;
  /** Legacy close handler — wizard wraps this step inside WizardFrame
   *  which handles cancel via Escape / backdrop. Retained so older
   *  call sites that mounted this directly still compile. */
  onClose?: () => void;
  onChooseType: (reportTypeId: ReportTypeId) => void;
  existingReportTypes: ReportTypeId[];
  /** Optional — retained for API compat; cards no longer render intent hints. */
  projectIntent?: ProjectIntent;
  existingVersionByType?: Partial<Record<ReportTypeId, number>>;
  siteReports?: SiteRagReport[];
  /** When true (the site's first report is mid-generation with nothing
   *  completed yet) the "Choose a report type to begin." lead-in is dropped
   *  so the copy doesn't contradict an on-screen progress card. The
   *  "You can run multiple report types…" clause always stays. Defaults to
   *  false → existing copy is unchanged. */
  firstRunGenerating?: boolean;
  /**
   * P2.1 — the site's Path-to-GO plan. A card the plan lanes available-later
   * shows the plan's own reason under it (no lane badge — owner, 2026-09-30);
   * an optional type carries an "Optional" chip. The user learns BEFORE
   * choosing a type that its source inputs are not ready — the same predicate
   * and strings the Generate stage renders. Cards are never disabled by an
   * input hint: the server chokepoint is the admission test, and the client
   * must not fabricate a block the server would not raise.
   * The ONE exception is the two-stage lock (`stage-locked`): the chokepoint
   * refuses a Stage 2 analysis without a promoted scenario through the same
   * predicate, so disabling the card mirrors the server rather than inventing.
   */
  reportPlan?: readonly ReportPlanItem[];
}

export function ReportTypeSelectStep({
  open = true,
  onChooseType,
  existingReportTypes: _existingReportTypes,
  existingVersionByType,
  siteReports,
  firstRunGenerating = false,
  reportPlan,
}: Props) {
  const derivedVersionByType = React.useMemo(() => {
    const out: Partial<Record<ReportTypeId, number>> = {};
    for (const report of siteReports ?? []) {
      const type = report.reportType as ReportTypeId | undefined;
      if (!type) continue;
      const version = typeof report.version === 'number' ? report.version : undefined;
      if (typeof version !== 'number') continue;
      out[type] = Math.max(out[type] ?? 0, version);
    }
    return out;
  }, [siteReports]);

  // `beta` (partially-implemented) report types are offered only to Deep
  // Atomic Admins; tenant users never see them in the picker. `coming_soon`
  // types still render (locked) for everyone via the card affordance.
  const isDatacrateAdmin = useIsDatacrateAdmin();

  if (!open) return null;
  const types = getAllReportTypes().filter(
    (t) =>
      !isLiveOnlyReportType(t.id)
      // A retired type is never offered — its reports stay readable elsewhere.
      && t.status !== 'retired'
      && (t.status !== 'beta' || isDatacrateAdmin),
  );
  const versionByType = existingVersionByType ?? derivedVersionByType;
  // TWO-STAGE PIPELINE — cards group under their stage header, Stage 1 first,
  // registry order within a stage. A type without a stage (an additional
  // analysis — Public Sentiment since 2026-09-30) trails under its own
  // "Additional analyses" header, the Analyses tab's name for the same group,
  // so it never reads as part of Stage 2.
  const stagedTypes = React.useMemo(() => {
    const ordered = [...types].sort((a, b) => (a.stage ?? 9) - (b.stage ?? 9));
    let last: DecisionStage | null | undefined;
    return ordered.map((type) => {
      const stage: DecisionStage | null = type.stage ?? null;
      const headed = stage !== last;
      last = stage;
      return { stage, headed, type };
    });
  }, [types]);

  return (
    <div data-testid="report-type-select-body">
      <div className="px-5 py-3 border-b border-border-subtle">
        <p className="text-xs text-text-muted max-w-md leading-relaxed">
          {!firstRunGenerating && 'Choose a report type to begin. '}You can run multiple report types on the same site.
        </p>
      </div>

      <div data-testid="report-type-grid">
        <div
          className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-3 w-full min-w-0"
          data-testid="report-type-card-grid"
          role="list"
        >
          {stagedTypes.map(({ stage, headed, type }) => {
            const selectable = type.status === 'active' || type.status === 'beta';
            const existingVersion = versionByType[type.id];
            const planItem = reportPlan?.find(item => item.type === type.id);
            const lane = planItem ? analysisLane(planItem) : null;
            const stageLocked = planItem?.eligibility?.reasonCode === 'stage-locked';
            const laneChip = lane === 'optional' ? 'Optional' : null;
            const laneReason = planItem && lane === 'available-later' ? laterLabel(planItem) : null;
            return (
              <React.Fragment key={type.id}>
                {headed && (
                  <p
                    className="col-span-full type-meta uppercase tracking-[0.13em] text-text-secondary pt-2 first:pt-0"
                    data-testid={stage !== null ? `report-type-stage-${stage}` : 'report-type-stage-additional'}
                  >
                    {stage !== null ? DECISION_STAGE_LABEL[stage] : ADDITIONAL_ANALYSES_LABEL}
                  </p>
                )}
              <div className="min-w-0 flex flex-col gap-1" data-lane={lane ?? undefined} data-stage={stage ?? undefined}>
                <ReportTypeLaunchCard
                  type={type}
                  disabled={!selectable || stageLocked}
                  {...(existingVersion !== undefined ? { existingVersion } : {})}
                  onClick={() => onChooseType(type.id)}
                  {...(laneChip
                    ? {
                      actions: (
                        <StatusPill
                          data-testid={`report-type-lane-${type.id}`}
                          variant="neutral"
                          className="type-body"
                        >
                          {laneChip}
                        </StatusPill>
                      ),
                    }
                    : {})}
                />
                {laneReason && (
                  <p
                    className="px-1 text-[11px] leading-snug text-text-secondary"
                    data-testid={`report-type-eligibility-${type.id}`}
                  >
                    {laneReason}
                  </p>
                )}
              </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}
